import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { auditTrail, type AuditRecord } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Pagination } from '@/shared/ui/Pagination';

/**
 * Trilha de auditoria.
 *
 * Responde quem fez o que, quando e de onde. Ela sempre existiu no banco, o que na
 * pratica quer dizer que respondia a quem tem acesso ao banco, e nao a quem precisa
 * da resposta: quem opera e quem audita.
 *
 * O conteudo de antes e depois nao aparece aqui. Ele pode conter o cadastro inteiro
 * de um cliente, e a consulta nao precisa dele para responder a pergunta. A tela de
 * logs (P-LOG) tem o periodo, a entidade, o conteudo para quem tem a permissao, os
 * acessos e as rotinas; esta aba continua e leva ate ela.
 *
 * A aba confere a funcionalidade Auditoria do plano e a permissao que o servidor
 * exige (auditoria.log.view), em vez de mostrar erro a quem nao contratou ou nao
 * pode (47, anexo C, Empresa 9).
 */

const limite = 25;

export function AuditTrailPanel() {
  const { can, hasFeature } = useAuth();
  const contratada = hasFeature('administracao.auditoria');
  const podeVer = contratada && can('auditoria.log.view');

  const [ator, setAtor] = useState('');
  const [modulo, setModulo] = useState('');
  const [offset, setOffset] = useState(0);

  const consulta = useQuery({
    queryKey: ['audit-logs', { ator, modulo, offset }],
    queryFn: ({ signal }) =>
      auditTrail.list({ actor: ator, module: modulo, limit: limite, offset }, signal),
    enabled: podeVer,
  });

  if (!contratada) {
    return (
      <Alert tone="info" title="Auditoria fora do plano da empresa">
        A trilha de auditoria faz parte dos planos professional e enterprise. Fale com
        quem administra a empresa.
      </Alert>
    );
  }

  if (!podeVer) {
    return (
      <Alert tone="info" title="Trilha indisponivel para o seu acesso">
        A consulta da trilha e restrita. Fale com quem administra a empresa.
      </Alert>
    );
  }

  const colunas: Column<AuditRecord>[] = [
    {
      key: 'quando',
      header: 'Data e hora',
      render: (linha) => (
        <span className="tabular-nums">{formatDateTime(linha.occurred_at)}</span>
      ),
    },
    {
      key: 'quem',
      header: 'Quem',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">
            {linha.actor_name || linha.actor_label || 'sem identificacao'}
          </p>
          {linha.actor_name && linha.actor_label ? (
            <p className="truncate text-2xs text-ink-500">{linha.actor_label}</p>
          ) : null}
          {linha.actor_type !== 'human' ? (
            <Badge tone="neutral">{linha.actor_type}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'acao',
      header: 'Acao',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{linha.action}</p>
          <p className="truncate text-2xs text-ink-500">{linha.entity_type}</p>
        </div>
      ),
    },
    {
      key: 'modulo',
      header: 'Modulo',
      hideBelow: 'md',
      render: (linha) => linha.module,
    },
    {
      key: 'origem',
      header: 'Origem',
      hideBelow: 'lg',
      render: (linha) =>
        linha.ip || <span className="text-ink-400">nao registrada</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <div className="w-56">
            <Field
              label="Quem"
              className="h-9"
              placeholder="Nome ou parte dele"
              value={ator}
              onChange={(evento) => {
                setAtor(evento.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div className="w-48">
            <Field
              label="Modulo"
              className="h-9"
              placeholder="operacoes, emissao..."
              value={modulo}
              onChange={(evento) => {
                setModulo(evento.target.value);
                setOffset(0);
              }}
            />
          </div>
          <span className="ml-auto self-end text-xs text-ink-600">
            {consulta.data?.total ?? 0} registros
          </span>
          <Link
            to="/logs"
            className="self-end text-xs font-semibold text-brand-600 underline-offset-2 hover:underline"
          >
            Abrir a tela de logs
          </Link>
        </div>

        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar a trilha">
            {consulta.error.message}
          </Alert>
        ) : null}

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(linha) => linha.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="lock"
              title={
                ator || modulo ? 'Nenhum registro encontrado' : 'Trilha ainda vazia'
              }
              description={
                ator || modulo
                  ? 'Ajuste os filtros para ver outros registros.'
                  : 'Cada acao que altera dado passa a aparecer aqui.'
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <p className="text-2xs text-ink-500">
        O conteudo alterado nao aparece na listagem. Ele pode conter o cadastro inteiro
        de um cliente, e a consulta nao precisa dele para responder quem fez o que.
        Periodo, cadastro, conteudo das alteracoes, acessos e rotinas ficam na{' '}
        <Link to="/logs" className="text-brand-600 underline-offset-2 hover:underline">
          tela de logs
        </Link>
        .
      </p>
    </div>
  );
}
